import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Employee } from '../types';

export function useEmployees(includeInactive: boolean = false) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      
      // Add retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let data, error;
      
      while (attempts < maxAttempts) {
        let query = supabase
          .from('employees')
          .select('*')
          .order('last_name', { ascending: true });
        
        // Only filter for active employees if includeInactive is false
        if (!includeInactive) {
          query = query.eq('is_active', true);
        }
        
        const result = await query;
        
        if (result.error) {
          if (attempts < maxAttempts - 1) {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
            continue;
          }
          error = result.error;
        } else {
          data = result.data;
          break;
        }
      }

      if (error) throw error;

      setEmployees(data.map(employee => ({
        id: employee.id,
        firstName: employee.first_name,
        lastName: employee.last_name,
        email: employee.email,
        position: employee.position,
        department: employee.department,
        salary: parseFloat(employee.salary) || 0,
        paymentType: employee.payment_type,
        startDate: employee.start_date,
        taxStatus: employee.tax_status,
        state: employee.state || '',
        stateRate: parseFloat(employee.state_rate) || 0,
        insuranceRate: parseFloat(employee.insurance_rate) || 0,
        retirementRate: parseFloat(employee.retirement_rate) || 0,
        federalTaxRate: employee.federal_tax_rate === null ? 22 : parseFloat(employee.federal_tax_rate),
        bonus: parseFloat(employee.bonus) || 0,
        isActive: employee.is_active
      })));
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch employees. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return { employees, loading, error, refetch: fetchEmployees };
}